/**
 * MODULE: _shared/frontend-shell/src/layout/shellAssistant/ShellAssistantEntityRouteBridge.tsx
 * PURPOSE: Push focused entity ids from URL paths into `routeMeta.selectedRecord` for Pages and Calendar [2.2.1345].
 * DEPENDENCIES: react, react-router-dom, ./ShellAssistantContext
 * PUBLIC_API: ShellAssistantEntityRouteBridge
 * SIDE_EFFECTS: `setRouteMeta` functional updates (`selectedRecord` only)
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 72/120
 */

import { useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ShellAssistantReactContext } from './ShellAssistantContext';

function selectedRecordFromPath(pathname: string): string | null {
  const path = (pathname || '').replace(/\/+$/, '');

  const pageMatch = path.match(/^\/workspaces\/[^/]+\/pages\/([^/]+)$/i);
  if (pageMatch?.[1] && pageMatch[1] !== 'new') {
    return decodeURIComponent(pageMatch[1]);
  }

  const eventMatch = path.match(/^\/calendar\/events\/([^/]+)$/i);
  if (eventMatch?.[1] && eventMatch[1] !== 'new') {
    return decodeURIComponent(eventMatch[1]);
  }

  return null;
}

export function ShellAssistantEntityRouteBridge() {
  const { pathname } = useLocation();
  const shellAssistant = useContext(ShellAssistantReactContext);
  const setRouteMeta = shellAssistant?.setRouteMeta;

  const selectedRecord = useMemo(() => selectedRecordFromPath(pathname), [pathname]);

  useEffect(() => {
    if (!setRouteMeta) return;
    setRouteMeta((prev) => {
      if (prev.selectedRecord === selectedRecord) return prev;
      return { ...prev, selectedRecord };
    });
  }, [selectedRecord, setRouteMeta]);

  return null;
}
